import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CARE_PLAN_TEXT_MAX_LENGTH, CARE_PLAN_TEXT_MAX_MESSAGE, CarePlanSectionProps, NA, NO, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, YES } from "../../../constants/constants";
import { convertIsoToDateOnly } from "../../../utils/stringUtils";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";

const SummarySection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);

  const toast = useRef<Toast>(null);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.SUMMARY,
    providerName: sectionData?.providerName || "",
    acesId: sectionData?.acesId || "",

    dateNCPStarted: sectionData?.dateNCPStarted ? new Date(sectionData?.dateNCPStarted) : null,
    dateNCPCompleted: sectionData?.dateNCPCompleted ? new Date(sectionData?.dateNCPCompleted) : null,
    assessmentDate: sectionData?.assessmentDate ? new Date(sectionData?.assessmentDate) : null,

    residentPronouns: sectionData?.residentPronouns || "",
    residentDateOfBirth: sectionData?.residentDateOfBirth ? new Date(sectionData?.residentDateOfBirth) : null,
    residentAge: sectionData?.residentAge || "",
    residentGender: sectionData?.residentGender || "",
    residentName: sectionData?.residentName || "",
    residentMoveInDate: sectionData?.residentMoveInDate ? new Date(sectionData?.residentMoveInDate) : null,
    residentDischargeDate: sectionData?.residentDischargeDate ? new Date(sectionData?.residentDischargeDate) : null,
    residentPrimaryLanguage: sectionData?.residentPrimaryLanguage || "",
    residentSpeakEnglish: sectionData?.residentSpeakEnglish || false,
    residentInterpreterNeeded: sectionData?.residentInterpreterNeeded || false,
    residentAllergies: sectionData?.residentAllergies || "",

    anyLegalDocuments: sectionData?.anyLegalDocuments || false,
    anyAdvancedDirectives: sectionData?.anyAdvancedDirectives || false,
    anyPOLSTForms: sectionData?.anyPOLSTForms || false,
    anyOtherLegalDocuments: sectionData?.anyOtherLegalDocuments || false,
    otherLegalDocumentsDetails: sectionData?.otherLegalDocumentsDetails || "",

    anySpecialtyCareNeeds: sectionData?.anySpecialtyCareNeeds || false,
    anyDementiaCareNeeds: sectionData?.anyDementiaCareNeeds || false,
    anyMentalHealthCareNeeds: sectionData?.anyMentalHealthCareNeeds || false,
    anyDevelopmentalDisabilityCareNeeds: sectionData?.anyDevelopmentalDisabilityCareNeeds || false,
    anyOtherSpecialtyCareNeeds: sectionData?.anyOtherSpecialtyCareNeeds || false,
    medicalDiagnosesDetails: sectionData?.medicalDiagnosesDetails || "",
    otherSpecialtyCareNeedsDetails: sectionData?.otherSpecialtyCareNeedsDetails || "",

    evacuationAssistanceRequired: sectionData?.evacuationAssistanceRequired || false,
    residentEvacuationAndSafetyInstructions: sectionData?.residentEvacuationAndSafetyInstructions || "",
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register } = useForm({ defaultValues });
  const watchAnyOtherLegalDocuments = watch("anyOtherLegalDocuments", defaultValues.anyOtherLegalDocuments);
  const watchAnyLegalDocuments = watch("anyLegalDocuments", defaultValues.anyLegalDocuments);
  const watchAnyOtherSpecialtyCareNeeds = watch("anyOtherSpecialtyCareNeeds", defaultValues.anyOtherSpecialtyCareNeeds);
  const watchAnySpecialtyCareNeeds = watch("anySpecialtyCareNeeds", defaultValues.anySpecialtyCareNeeds);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>

      <Card className="resident-careplan-card" footer={actions()} header={actions()} >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Provider details</Tag></Divider>
              {
                enableEditing ? (
                  <>
                    <div className="flex flex-wrap justify-content-center align-items-center">
                      <span className="p-float-label">
                        <Controller name="providerName" control={control} rules={{
                          required: 'Provider name is required.',
                          maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE }
                        }} render={({ field, fieldState }) => (
                          <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                        )} />
                        <label htmlFor="providerName" className={classNames({ 'p-error': errors.providerName })}>Provider name</label>
                      </span>
                      {getFormErrorMessage('providerName')}
                    </div>
                  </>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex flex-column"><strong>Provider name: </strong> {sectionData?.providerName || NA}</div>
                  </div>
                )
              }
            </div>

            <div className="justify-content-center align-items-center flex-wrap grid">
              <Divider><Tag severity="warning">Basic Dates</Tag></Divider>
              {
                enableEditing ? (
                  <div className="grid">
                    <div className="col-12 md:col-6 lg:col-3">
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="acesId" className={classNames({ 'p-error': errors.acesId })}>
                          ACES ID
                        </label>
                      </span>
                      <span>
                        <Controller name="acesId" control={control} rules={{
                          maxLength: { value: 50, message: 'ACES ID must be at most 50 characters long.' }
                        }} render={({ field, fieldState }) => (
                          <InputText id={field.name} {...field} className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                        )} />
                      </span>
                      {getFormErrorMessage('acesId')}
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="assessmentDate" className={classNames({ 'p-error': errors.assessmentDate })}>
                          Assessment date
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="assessmentDate" control={control} rules={{
                          required: 'Assessment date is required.',
                        }} render={({ field, fieldState }) => (
                          <Calendar id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)}
                            className={classNames('w-full', { 'p-invalid': fieldState.invalid })} maxDate={new Date()} />
                        )} />
                      </span>
                      {getFormErrorMessage('assessmentDate')}
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="dateNCPStarted" className={classNames({ 'p-error': errors.dateNCPStarted })}>
                          Date NCP started
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="dateNCPStarted" control={control} rules={{
                          required: 'Date NCP Started is required.',
                        }} render={({ field, fieldState }) => (
                          <Calendar id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)}
                            className={classNames('w-full', { 'p-invalid': fieldState.invalid })} maxDate={new Date()} />
                        )} />
                      </span>
                      {getFormErrorMessage('dateNCPStarted')}
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="dateNCPCompleted" className={classNames({ 'p-error': errors.dateNCPCompleted })}>
                          Date NCP completed
                        </label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.NCP_COMPLETE_DATE) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <span className="p-float-label">
                        <Controller name="dateNCPCompleted" control={control} rules={{
                          required: 'Date NCP completed is required.',
                        }} render={({ field, fieldState }) => (
                          <Calendar id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)}
                            className={classNames('w-full', { 'p-invalid': fieldState.invalid })} />
                        )} />
                      </span>
                      {getFormErrorMessage('dateNCPCompleted')}
                    </div>
                  </div>) : (
                  <div className=" justify-content-center align-items-center grid">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Assessment date: </strong> {sectionData?.assessmentDate ? new Date(sectionData?.assessmentDate).toLocaleDateString() : NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Date NCP started: </strong> {sectionData?.dateNCPStarted ? new Date(sectionData?.dateNCPStarted).toLocaleDateString() : NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Date NCP completed: </strong> {sectionData?.dateNCPCompleted ? new Date(sectionData?.dateNCPCompleted).toLocaleDateString() : NA}</div>
                  </div>)
              }
            </div>

            <div className="justify-content-center align-items-center flex-wrap grid">
              <Divider><Tag severity="warning">Resident details</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentName" control={control} rules={{
                          required: 'Resident name is required.',
                          maxLength: { value: 50, message: 'Resident name must be at most 50 characters long.' }
                        }} render={({ field, fieldState }) => (
                          <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                        )} />
                        <label htmlFor="residentName" className={classNames({ 'p-error': errors.residentName })}>Resident name</label>
                      </span>
                      {getFormErrorMessage('residentName')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentPronouns" control={control} rules={{
                          maxLength: { value: 50, message: 'Resident pronouns must be at most 50 characters long.' }
                        }} render={({ field, fieldState }) => (
                          <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Mr, Mrs., Miss" />
                        )} />
                        <label htmlFor="residentPronouns" className={classNames({ 'p-error': errors.residentPronouns })}>Pronouns</label>
                      </span>
                      {getFormErrorMessage('residentPronouns')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentDateOfBirth" control={control} render={({ field, fieldState }) => (
                          <Calendar id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)}
                            className={classNames({ 'p-invalid': fieldState.invalid })} maxDate={new Date()} />
                        )} />
                        <label htmlFor="residentDateOfBirth" className={classNames({ 'p-error': errors.residentDateOfBirth })}>Date of birth</label>
                      </span>
                      {getFormErrorMessage('residentDateOfBirth')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentAge" control={control} rules={{
                          required: 'Resident age is required.',
                          maxLength: { value: 50, message: 'Resident age must be at most 50 characters long.' }
                        }} render={({ field, fieldState }) => (
                          <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Age" />
                        )} />
                        <label htmlFor="residentAge" className={classNames({ 'p-error': errors.residentAge })}>Age</label>
                      </span>
                      {getFormErrorMessage('residentAge')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentGender" control={control} rules={{
                          required: 'Resident gender is required.',
                          maxLength: { value: 50, message: 'Resident gender must be at most 50 characters long.' }
                        }} render={({ field, fieldState }) => (
                          <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="Gender" />
                        )} />
                        <label htmlFor="residentGender" className={classNames({ 'p-error': errors.residentGender })}>Gender</label>
                      </span>
                      {getFormErrorMessage('residentGender')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentMoveInDate" control={control} rules={{
                          required: 'Resident move-in date is required.',
                        }} render={({ field, fieldState }) => (
                          <Calendar id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)}
                            className={classNames({ 'p-invalid': fieldState.invalid })} />
                        )} />
                        <label htmlFor="residentMoveInDate" className={classNames({ 'p-error': errors.residentMoveInDate })}>Move-in date</label>
                      </span>
                      {getFormErrorMessage('residentMoveInDate')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentDischargeDate" control={control} render={({ field, fieldState }) => (
                          <Calendar id={field.name} value={field.value} onChange={(e) => field.onChange(e.value)}
                            className={classNames({ 'p-invalid': fieldState.invalid })} />
                        )} />
                        <label htmlFor="residentDischargeDate" className={classNames({ 'p-error': errors.residentDischargeDate })}>Discharge date</label>
                      </span>
                      {getFormErrorMessage('residentDischargeDate')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentPrimaryLanguage" control={control} rules={{
                          required: 'Resident primary language is required.',
                          maxLength: { value: 50, message: 'Resident primary language must be at most 50 characters long.' }
                        }} render={({ field, fieldState }) => (
                          <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} placeholder="English, Spanish, etc." />
                        )} />
                        <label htmlFor="residentPrimaryLanguage" className={classNames({ 'p-error': errors.residentPrimaryLanguage })}>Primary language</label>
                      </span>
                      {getFormErrorMessage('residentPrimaryLanguage')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.residentSpeakEnglish })}>Speak english:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="residentSpeakEnglish"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('residentSpeakEnglish')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.residentInterpreterNeeded })}>Interpreter needed:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="residentInterpreterNeeded"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('residentInterpreterNeeded')}
                    </div>

                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="p-float-label">
                        <Controller name="residentAllergies" control={control}
                          rules={{
                            maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE }
                          }}
                          render={({ field, fieldState }) => (
                            <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                        <label htmlFor="residentAllergies" className={classNames({ 'p-error': errors.residentAllergies })}>Allergies</label>
                      </span>
                      {getFormErrorMessage('residentAllergies')}
                    </div>
                  </div>) : (
                  <div className=" justify-content-center align-items-center grid">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>ACES ID: </strong> {sectionData?.acesId || NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident name: </strong> {sectionData?.residentName || NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Pronouns: </strong> {sectionData?.residentPronouns || NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Date of birth: </strong> {sectionData?.residentDateOfBirth ? convertIsoToDateOnly(sectionData?.residentDateOfBirth) : NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Age: </strong> {sectionData?.residentAge || NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Gender: </strong> {sectionData?.residentGender || NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Move-in date: </strong> {sectionData?.residentMoveInDate ? convertIsoToDateOnly(sectionData?.residentMoveInDate) : NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Discharge date: </strong> {sectionData?.residentDischargeDate ? convertIsoToDateOnly(sectionData?.residentDischargeDate) : NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Primary language: </strong> {sectionData?.residentPrimaryLanguage || NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Speak english: </strong> {sectionData?.residentSpeakEnglish ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Interpreter needed: </strong> {sectionData?.residentInterpreterNeeded ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Allergies: </strong> {sectionData?.residentAllergies || NA}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>&nbsp;</strong></div>
                  </div>
                )
              }
            </div>

            <div className="justify-content-center align-items-center flex-wrap grid">
              <Divider><Tag severity="warning">Legal documents</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyLegalDocuments })}>Any legal documents:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyLegalDocuments"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyLegalDocuments')}
                    </div>

                    {watchAnyLegalDocuments && (
                      <>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyAdvancedDirectives })}>Any advanced directives:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyAdvancedDirectives"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyAdvancedDirectives')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyPOLSTForms })}>Any POLST forms:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyPOLSTForms"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyPOLSTForms')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyOtherLegalDocuments })}>Any other legal documents:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyOtherLegalDocuments"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyOtherLegalDocuments')}
                        </div>
                        {
                          watchAnyOtherLegalDocuments && (
                            <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                              <span className="p-float-label">
                                <Controller name="otherLegalDocumentsDetails" control={control}
                                  rules={{
                                    required: 'Please explain other legal documents details',
                                    maxLength: {
                                      value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE
                                    }
                                  }}
                                  render={({ field, fieldState }) => (
                                    <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                  )} />
                                <label htmlFor="otherLegalDocumentsDetails" className={classNames({ 'p-error': errors.otherLegalDocumentsDetails })}>Other legal documents details</label>
                              </span>
                              {getFormErrorMessage('otherLegalDocumentsDetails')}
                            </div>
                          )
                        }
                      </>
                    )}
                  </div>) : (
                  <div className=" justify-content-center align-items-center flex-wrap grid">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any legal documents: </strong> {sectionData?.anyLegalDocuments ? YES : NO}</div>
                    {sectionData?.anyLegalDocuments && (
                      <>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any advanced directives: </strong> {sectionData?.anyAdvancedDirectives ? YES : NO}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any POLST forms: </strong> {sectionData?.anyPOLSTForms ? YES : NO}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other legal documents: </strong> {sectionData?.anyOtherLegalDocuments ? YES : NO}</div>
                        {
                          sectionData?.anyOtherLegalDocuments && (
                            <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                              <strong>Other legal documents details: </strong>
                              <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.otherLegalDocumentsDetails || NA}</span>
                            </div>
                          )
                        }
                      </>
                    )}
                  </div>)
              }
            </div>

            <div className="justify-content-center align-items-center flex-wrap grid">
              <Divider><Tag severity="warning">Special needs</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anySpecialtyCareNeeds })}>Any special needs:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anySpecialtyCareNeeds"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anySpecialtyCareNeeds')}
                    </div>

                    {watchAnySpecialtyCareNeeds && (
                      <>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyDementiaCareNeeds })}>Any dementia care needs:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyDementiaCareNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyDementiaCareNeeds')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyMentalHealthCareNeeds })}>Any mental health care needs:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyMentalHealthCareNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyMentalHealthCareNeeds')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyDevelopmentalDisabilityCareNeeds })}>Any developmental disability care needs:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyDevelopmentalDisabilityCareNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyDevelopmentalDisabilityCareNeeds')}
                        </div>
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                          <label className={classNames({ 'p-error': errors.anyOtherSpecialtyCareNeeds })}>Any other specialty care needs:</label>
                          <div className="inline-flex m-2 span-color-white">
                            <Controller
                              name="anyOtherSpecialtyCareNeeds"
                              control={control}
                              render={({ field }) => (
                                <ToggleButton onLabel="Yes" offLabel="No"
                                  {...field}
                                  checked={field.value}
                                  onChange={(e) => field.onChange(e.value)} />
                              )}
                            />
                          </div>
                          {getFormErrorMessage('anyOtherSpecialtyCareNeeds')}
                        </div>
                        {
                          watchAnyOtherSpecialtyCareNeeds && (
                            <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                              <span className="p-float-label">
                                <Controller name="otherSpecialtyCareNeedsDetails" control={control}
                                  rules={{
                                    required: 'Please explain other specialty care needs details',
                                    maxLength: {
                                      value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE
                                    }
                                  }}
                                  render={({ field, fieldState }) => (
                                    <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                  )} />
                                <label htmlFor="otherSpecialtyCareNeedsDetails" className={classNames({ 'p-error': errors.otherSpecialtyCareNeedsDetails })}>Other specialty care needs details</label>
                              </span>
                              {getFormErrorMessage('otherSpecialtyCareNeedsDetails')}
                            </div>
                          )
                        }
                        <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(100% - 1em)" }}>
                          <span className="p-float-label">
                            <Controller name="medicalDiagnosesDetails" control={control}
                              rules={{
                                maxLength: {
                                  value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE
                                }
                              }}
                              render={({ field, fieldState }) => (
                                <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                              )} />
                            <label htmlFor="medicalDiagnosesDetails" className={classNames({ 'p-error': errors.medicalDiagnosesDetails })}>Medical diagnoses</label>
                          </span>
                          {getFormErrorMessage('medicalDiagnosesDetails')}
                        </div>
                      </>
                    )}
                  </div>) : (
                  <div className=" justify-content-center align-items-center flex-wrap grid">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any special needs: </strong> {sectionData?.anySpecialtyCareNeeds ? YES : NO}</div>
                    {sectionData?.anySpecialtyCareNeeds && (
                      <>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any dementia care needs: </strong> {sectionData?.anyDementiaCareNeeds ? YES : NO}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any mental health care needs: </strong> {sectionData?.anyMentalHealthCareNeeds ? YES : NO}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any developmental disability care needs: </strong> {sectionData?.anyDevelopmentalDisabilityCareNeeds ? YES : NO}</div>
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any other specialty care needs: </strong> {sectionData?.anyOtherSpecialtyCareNeeds ? YES : NO}</div>
                        {sectionData?.anyOtherSpecialtyCareNeeds && (
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                            <strong>Other specialty care needs details: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.otherSpecialtyCareNeedsDetails || NA}</span>
                          </div>
                        )}
                        <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(100% - 1em)", whiteSpace: "pre-wrap" }}>
                          <strong>Medical diagnoses: </strong>
                          <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.medicalDiagnosesDetails || NA}</span>
                        </div>
                      </>
                    )
                    }
                  </div>)
              }
            </div>

            <div className="justify-content-center align-items-center flex-wrap grid">
              <Divider><Tag severity="warning">Evacuation and safety instructions</Tag></Divider>
              {
                enableEditing ? (
                  <>
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.evacuationAssistanceRequired })}>Evacuation assistance required:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="evacuationAssistanceRequired"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('evacuationAssistanceRequired')}
                    </div>
                    <div className="col flex-column flex-wrap mt-2" style={{ width: "100%" }}>
                      <span className="p-float-label">
                        <Controller name="residentEvacuationAndSafetyInstructions"
                          rules={{
                            maxLength: { value: CARE_PLAN_TEXT_MAX_LENGTH, message: CARE_PLAN_TEXT_MAX_MESSAGE }
                          }}
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                        <label htmlFor="residentEvacuationAndSafetyInstructions" className={classNames({ 'p-error': errors.residentEvacuationAndSafetyInstructions })}>Resident evacuation and safety instructions</label>
                      </span>
                      {getFormErrorMessage('residentEvacuationAndSafetyInstructions')}
                    </div>
                  </>) : (
                  <div className=" justify-content-center align-items-center flex-wrap grid">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Evacuation assistance required: </strong> {sectionData?.evacuationAssistanceRequired ? YES : NO}</div>
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Resident evacuation and safety instructions: </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{sectionData?.residentEvacuationAndSafetyInstructions || NA}</span>
                    </div>
                  </div>)
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default SummarySection;


